import React, { useState, useEffect, useMemo } from 'react';
import { resolveElementStyle, buildPanelStyle } from '../../utils/mobileStyleUtils';

const API_BASE = process.env.REACT_APP_API_BASE || (window.location.port === '3000' ? `http://${window.location.hostname}:8000` : '');

export default function MobileClaimTab({ onCardClaimed, initialCode = '', mobileStyles = null }) {
  const [claimToken, setClaimToken] = useState(initialCode);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [successData, setSuccessData] = useState(null);

  const resolvedPanels = useMemo(() => resolveElementStyle(mobileStyles, 'end_user:claim', 'content_panels'), [mobileStyles]);
  const resolvedTypo = useMemo(() => resolveElementStyle(mobileStyles, 'end_user:claim', 'typography'), [mobileStyles]);
  const customPanelStyle = useMemo(() => buildPanelStyle(resolvedPanels.style), [resolvedPanels]);
  const typoConfig = resolvedTypo.style || {};

  // Check URL query parameters for ?claim=TOKEN or #claim=TOKEN on mount
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const urlToken = params.get('claim') || params.get('token');
      if (urlToken && !claimToken) {
        setClaimToken(urlToken);
      }
    } catch (_) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFormatToken = (raw) => {
    // Keep numbers and letters, format nicely
    const clean = raw.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (clean.length > 3) {
      return `${clean.slice(0, 3)}-${clean.slice(3, 6)}`;
    }
    return clean;
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setErrorMsg(null);
    setClaimToken(handleFormatToken(val));
  };

  const handlePaste = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.readText) {
        const text = await navigator.clipboard.readText();
        if (text) {
          setErrorMsg(null);
          setClaimToken(handleFormatToken(text));
        }
      }
    } catch (_) {}
  };

  const handleSubmitClaim = async (e) => {
    if (e) e.preventDefault();
    if (!claimToken || claimToken.trim().length < 4) {
      setErrorMsg('Please enter a valid claim verification code.');
      return;
    }

    setSubmitting(true);
    setErrorMsg(null);

    try {
      const res = await fetch(`${API_BASE}/api/v1/coin-vault/claim-card`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('token') || localStorage.getItem('token') || ''}`,
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify({ token: claimToken })
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to claim card. Please check code.');
      }

      setSuccessData(data);
      if (onCardClaimed) {
        onCardClaimed(data.coin);
      }
    } catch (err) {
      setErrorMsg(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      padding: '16px 20px 100px 20px',
      boxSizing: 'border-box',
      color: typoConfig.body_color || '#f8fafc',
      fontFamily: typoConfig.font_family || "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif"
    }}>
      {/* Header */}
      <div style={{ marginBottom: '18px' }}>
        <h2 style={{
          fontSize: '20px',
          fontWeight: 800,
          margin: '0 0 6px 0',
          color: typoConfig.heading_color || '#f8fafc',
          fontFamily: typoConfig.font_family
        }}>
          Claim a Trophy Card
        </h2>
        <p style={{
          fontSize: '12.5px',
          color: typoConfig.body_color || '#94a3b8',
          margin: 0,
          lineHeight: 1.5,
          fontFamily: typoConfig.font_family
        }}>
          If your trophy card was registered under a different email address, enter the 6-digit verification code sent to that email to claim it into this account.
        </p>
      </div>

      {successData ? (
        <div style={{
          background: 'rgba(16, 185, 129, 0.12)',
          border: '1px solid rgba(16, 185, 129, 0.35)',
          borderRadius: '16px',
          padding: '24px 20px',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '14px',
          boxShadow: '0 8px 30px rgba(16, 185, 129, 0.2)'
        }}>
          <div style={{
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            background: '#10b981',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '26px'
          }}>
            ✓
          </div>

          <div>
            <h3 style={{ fontSize: '18px', fontWeight: 800, color: '#34d399', margin: '0 0 4px 0' }}>
              Card Claimed Successfully!
            </h3>
            <p style={{ fontSize: '13px', color: '#e2e8f0', margin: 0 }}>
              {successData.message}
            </p>
          </div>

          {successData.coin?.preview_front_url && (
            <img
              src={successData.coin.preview_front_url}
              alt="Claimed Card"
              style={{
                width: '120px',
                aspectRatio: '350 / 490',
                objectFit: 'contain',
                borderRadius: '8px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.6)'
              }}
            />
          )}

          <button
            type="button"
            onClick={() => {
              setSuccessData(null);
              setClaimToken('');
              if (onCardClaimed) onCardClaimed(successData.coin);
            }}
            style={{
              background: 'linear-gradient(135deg, #00c6ff 0%, #0072ff 100%)',
              color: '#ffffff',
              border: 'none',
              borderRadius: '9999px',
              padding: '12px 28px',
              fontSize: '13px',
              fontWeight: 800,
              cursor: 'pointer',
              boxShadow: '0 4px 16px rgba(0, 198, 255, 0.35)',
              marginTop: '8px'
            }}
          >
            Open in My Vault
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmitClaim} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Claim Code Input Card */}
          <div style={{
            ...customPanelStyle,
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}>
            <label style={{
              fontSize: '11px',
              fontWeight: 800,
              color: typoConfig.accent_color || '#38bdf8',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              fontFamily: typoConfig.font_family
            }}>
              6-Digit Claim Code
            </label>

            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="text"
                autoFocus
                placeholder="582-910"
                value={claimToken}
                onChange={handleInputChange}
                maxLength={7}
                style={{
                  flex: 1,
                  background: 'rgba(0, 0, 0, 0.4)',
                  border: '1.5px solid rgba(0, 198, 255, 0.4)',
                  borderRadius: '12px',
                  padding: '12px 16px',
                  fontSize: '22px',
                  fontWeight: 800,
                  letterSpacing: '0.12em',
                  color: '#ffffff',
                  textAlign: 'center',
                  fontFamily: 'monospace',
                  outline: 'none'
                }}
              />

              <button
                type="button"
                onClick={handlePaste}
                style={{
                  background: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.16)',
                  color: '#cbd5e1',
                  borderRadius: '12px',
                  padding: '0 16px',
                  fontSize: '12px',
                  fontWeight: 700,
                  cursor: 'pointer'
                }}
              >
                Paste
              </button>
            </div>

            {errorMsg && (
              <div style={{ color: '#f87171', fontSize: '12px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span>⚠️</span>
                <span>{errorMsg}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={submitting || !claimToken}
              style={{
                background: submitting
                  ? '#334155'
                  : 'linear-gradient(135deg, #00c6ff 0%, #0072ff 100%)',
                color: '#ffffff',
                border: 'none',
                borderRadius: '12px',
                padding: '14px',
                fontSize: '14px',
                fontWeight: 800,
                cursor: submitting ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                marginTop: '6px',
                boxShadow: '0 6px 20px rgba(0, 198, 255, 0.35)'
              }}
            >
              {submitting ? 'Verifying Code...' : 'Claim Card & Add to Vault'}
            </button>
          </div>

          {/* Educational Explainer */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.02)',
            border: '1px solid rgba(255, 255, 255, 0.06)',
            borderRadius: '14px',
            padding: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
          }}>
            <div style={{ fontSize: '12px', fontWeight: 700, color: '#f8fafc' }}>
              How does claiming work?
            </div>
            <div style={{ fontSize: '11.5px', color: '#94a3b8', lineHeight: 1.5 }}>
              1. Tap <strong>Claim Card</strong> on any shared card view.<br />
              2. A verification code will be sent to the trophy owner's registered email address.<br />
              3. Enter that code here to permanently link the card to your account.
            </div>
          </div>
        </form>
      )}
    </div>
  );
}
